import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ActiveTimer, StopTimerResult, TaskTimeSummary } from "../lib/types";

export function useTaskTimeEntries(taskId?: string) {
  return useQuery({
    queryKey: ["time-entries", taskId],
    queryFn: async () => (await api.get<TaskTimeSummary>(`/tasks/${taskId}/time-entries`)).data,
    enabled: !!taskId,
    refetchInterval: 30_000,
  });
}

export function useActiveTimers() {
  return useQuery({
    queryKey: ["time-entries", "active"],
    queryFn: async () => (await api.get<ActiveTimer[]>("/time-entries/active")).data,
    refetchInterval: 20_000,
  });
}

/**
 * While the app is open with a running timer, tells the server so every 30s. If the browser/tab is
 * closed the pings stop and the server auto-stops the timer at the last ping.
 */
export function useTimerHeartbeat() {
  const { data: timers = [] } = useActiveTimers();
  const hasRunning = timers.length > 0;
  useEffect(() => {
    if (!hasRunning) return;
    const ping = () => void api.post("/time-entries/heartbeat").catch(() => undefined);
    ping();
    const id = window.setInterval(ping, 30_000);
    return () => window.clearInterval(id);
  }, [hasRunning]);
}

export interface TaskTimeStatus {
  totalSeconds: number;
  running: { userId: string; name: string; avatarUrl?: string; startedAt: string }[];
  paused: { userId: string; type: "break" | "meeting"; startedAt: string }[];
}

/** Live timer state for a batch of tasks, keyed by task id. */
export function useTasksTimeStatus(taskIds: string[]) {
  const ids = Array.from(new Set(taskIds)).sort().join(",");
  return useQuery({
    queryKey: ["time-entries", "by-tasks", ids],
    queryFn: async () => (await api.get<Record<string, TaskTimeStatus>>("/time-entries/by-tasks", { params: { ids } })).data,
    enabled: ids.length > 0,
    refetchInterval: 20_000,
  });
}

function useInvalidateTimeEntries(taskId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["time-entries", taskId] });
    queryClient.invalidateQueries({ queryKey: ["time-entries", "active"] });
    queryClient.invalidateQueries({ queryKey: ["time-entries", "by-tasks"] });
  };
}

export function useStartTimer(taskId: string) {
  const invalidate = useInvalidateTimeEntries(taskId);
  return useMutation({
    mutationFn: async () => (await api.post(`/tasks/${taskId}/time-entries/start`)).data,
    onSuccess: invalidate,
  });
}

export function useStopTimer(taskId: string) {
  const invalidate = useInvalidateTimeEntries(taskId);
  return useMutation({
    mutationFn: async (note: string) => (await api.post<StopTimerResult>(`/tasks/${taskId}/time-entries/stop`, { note })).data,
    onSuccess: invalidate,
  });
}

export function useLogManualTime(taskId: string) {
  const invalidate = useInvalidateTimeEntries(taskId);
  return useMutation({
    mutationFn: async (payload: { startedAt: string; endedAt: string; note?: string }) =>
      (await api.post(`/tasks/${taskId}/time-entries/manual`, payload)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateTimeEntry(taskId: string) {
  const invalidate = useInvalidateTimeEntries(taskId);
  return useMutation({
    mutationFn: async ({ entryId, ...payload }: { entryId: string; startedAt: string; endedAt: string; note?: string }) =>
      (await api.patch(`/tasks/${taskId}/time-entries/${entryId}`, payload)).data,
    onSuccess: invalidate,
  });
}

export function useDeleteTimeEntry(taskId: string) {
  const invalidate = useInvalidateTimeEntries(taskId);
  return useMutation({
    mutationFn: async (entryId: string) => (await api.delete(`/tasks/${taskId}/time-entries/${entryId}`)).data,
    onSuccess: invalidate,
  });
}
